import { request } from "./request";

export default class FeaturesAPI {
  /**
   * Get all creators with feature flags
   * @param {Object} data - Request data: {
   *   search: string,
   *   page: number,
   *   limit: number,
   *   userTier?: string,
   *   planType?: string,
   *   createdAtFrom?: string,
   *   createdAtTo?: string,
   *   ADD_CUSTOMERS?: string,
   *   CHECKOUT_TIMER?: string,
   *   PREMIUM_TEMPLATES?: string,
   *   SPARKS?: string,
   *   THEME_AI?: string,
   *   APP_BUILDER_AI?: string,
   *   WEBSITE_BUILDER_AI?: string,
   *   BANNER_AI?: string,
   *   MANAGE_SECRET?: string,
   *   ENABLE_TEMPLATE?: string,
   *   STORAGE_LIMIT_FROM?: string,
   *   STORAGE_LIMIT_TO?: string
   * }
   */
  static getAllCreatorsWithFeatureFlag(data) {
    const {
      search = "",
      page = 1,
      limit = 10,
      userTier,
      planType,
      createdAtFrom,
      createdAtTo,
      ADD_CUSTOMERS,
      CHECKOUT_TIMER,
      PREMIUM_TEMPLATES,
      SPARKS,
      STORAGE_LIMIT_FROM,
      STORAGE_LIMIT_TO,
      WEBINAR,
      COURSE_RECOMMENDATION,
      FLEXI_PAYMENT,
      INSTANT_WITHDRAWAL,
      THEME_AI,
      APP_BUILDER_AI,
      WEBSITE_BUILDER_AI,
      BANNER_AI,
      MANAGE_SECRET,
      ENABLE_TEMPLATE,
    } = data;

    const query = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      ...(search && { search }),
      ...(userTier && { userTier }),
      ...(planType && { planType }),
      ...(createdAtFrom && { createdAtFrom }),
      ...(createdAtTo && { createdAtTo }),
      ...(ADD_CUSTOMERS && { ADD_CUSTOMERS }),
      ...(CHECKOUT_TIMER && { CHECKOUT_TIMER }),
      ...(PREMIUM_TEMPLATES && { PREMIUM_TEMPLATES }),
      ...(SPARKS && { SPARKS }),
      ...(STORAGE_LIMIT_FROM && { STORAGE_LIMIT_FROM }),
      ...(STORAGE_LIMIT_TO && { STORAGE_LIMIT_TO }),
      ...(WEBINAR && { WEBINAR }),
      ...(COURSE_RECOMMENDATION && { COURSE_RECOMMENDATION }),
      ...(FLEXI_PAYMENT && { FLEXI_PAYMENT }),
      ...(INSTANT_WITHDRAWAL && { INSTANT_WITHDRAWAL }),
      // MANGO_AI is intentionally absent: the backend ignores ?MANGO_AI=true,
      // so chat access cannot be filtered from this list
      ...(THEME_AI && { THEME_AI }),
      ...(APP_BUILDER_AI && { APP_BUILDER_AI }),
      ...(WEBSITE_BUILDER_AI && { WEBSITE_BUILDER_AI }),
      ...(BANNER_AI && { BANNER_AI }),
      ...(MANAGE_SECRET && { MANAGE_SECRET }),
      ...(ENABLE_TEMPLATE && { ENABLE_TEMPLATE }),
    });

    return request({
      url: `/admin/all-creators-with-feature-flag?${query}`,
      method: "GET",
    });
  }

  /**
   * Edit feature flag for multiple users.
   * `standardWithdrawalDelay` (hours, 0 = no delay) is optional per entry and
   * updates the host-level standard-withdrawal delay on the CustomHost.
   * The seven Mango AI switches are features of their own (MANGO_AI, THEME_AI,
   * APP_BUILDER_AI, WEBSITE_BUILDER_AI, BANNER_AI, MANAGE_SECRET,
   * ENABLE_TEMPLATE), each with enabled/expiryAt/allowedUsers/source. MANGO_AI
   * is merged field by field and also holds aicreditValue, which is an amount
   * ADDED to the balance rather than the balance to set; every other feature is
   * replaced as a whole object, so send the complete object you want stored.
   * `aiVerse` is optional per entry and toggles the AI Verse add-on
   * (addOnsConfig.AIVERSE.enabled on the user document). It sits beside
   * email/host rather than inside featuresConfig, must be a real boolean —
   * anything else rejects the whole request with a 400 — and when omitted
   * leaves the add-on untouched.
   * @param {Object} data - Request data: {
   *   users: Array<{ email: string, host: string, featuresConfig: IFeaturesConfig, standardWithdrawalDelay?: number, aiVerse?: boolean }>
   * }
   */
  static editFeatureFlagForUsers(data) {
    const { users } = data;

    return request({
      url: "/admin/edit-feature-flag-for-users",
      method: "POST",
      data: {
        users,
      },
    });
  }

  /**
   * Validate CSV entries for manage features.
   * Returns enriched data (name, phone, featuresConfig, userTier, etc.)
   * for valid entries, and error messages for invalid ones.
   * @param {Object} data - { entries: Array<{ email: string, host: string }> }
   */
  static validateManageFeatureEntries(data) {
    return request({
      url: "/api/v1/admin/validate/manage-feature/entries",
      method: "POST",
      data,
    });
  }

  /**
   * Install custom app for multiple users
   * @param {Object} data - Request data: {
   *   host: string, app: string
   * }
   */
  static installCustomAppInHost(data) {
    return request({
      url: "/api/v1/admin/tools/install-custom-app",
      method: "POST",
      data: {
        ...data,
        staging: false,
      },
    });
  }

  /**
   * Uninstall custom app for multiple users
   * @param {Object} data - Request data: {
   *   host: string, app: string
   * }
   */
  static uninstallCustomAppFromHost(data) {
    return request({
      url: "/api/v1/admin/tools/uninstall-custom-app",
      method: "POST",
      data: {
        ...data,
        staging: false,
      },
    });
  }
}
